/**
 * Turn a picked/captured file into something worth sending from a building site:
 * photos are resized in the browser (longest side 1600 px, JPEG ~0.8) unless the
 * project keeps originals; every file gets a SHA-256 (evidence integrity — the
 * server records it) and photos get a small local thumbnail.
 *
 * Canvas/OffscreenCanvas only: no image library in the bundle.
 */
import type { MediaKind } from '@/lib/api/field-types';

export const MAX_EDGE_PX = 1600;
export const THUMB_EDGE_PX = 320;
export const JPEG_QUALITY = 0.8;

const IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];
export const ACCEPTED_MEDIA = [...IMAGE_TYPES, 'application/pdf', 'video/mp4'].join(',');

export class MediaRejected extends Error {
  constructor(readonly reason: 'type' | 'unreadable') {
    super(reason === 'type' ? 'Unsupported file type' : 'The file could not be read');
  }
}

export type PreparedMedia = {
  blob: Blob;
  thumbnail: Blob | null;
  mime_type: string;
  size: number;
  sha256: string;
  kind: MediaKind;
  file_name: string;
};

export function kindFor(mime: string): MediaKind | null {
  if (IMAGE_TYPES.includes(mime)) return 'photo';
  if (mime === 'video/mp4') return 'video';
  if (mime === 'application/pdf') return 'document';
  return null;
}

export async function sha256Hex(blob: Blob): Promise<string> {
  const digest = await crypto.subtle.digest('SHA-256', await blob.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join('');
}

/** Resize to fit `edge` (never upscale) and encode as JPEG. */
export async function resizeImage(source: Blob, edge: number, quality: number): Promise<Blob> {
  const bitmap = await createImageBitmap(source);
  try {
    const scale = Math.min(1, edge / Math.max(bitmap.width, bitmap.height));
    const width = Math.max(1, Math.round(bitmap.width * scale));
    const height = Math.max(1, Math.round(bitmap.height * scale));
    if (typeof OffscreenCanvas !== 'undefined') {
      const canvas = new OffscreenCanvas(width, height);
      const context = canvas.getContext('2d');
      if (!context) throw new MediaRejected('unreadable');
      context.drawImage(bitmap, 0, 0, width, height);
      return await canvas.convertToBlob({ type: 'image/jpeg', quality });
    }
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) throw new MediaRejected('unreadable');
    context.drawImage(bitmap, 0, 0, width, height);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : reject(new MediaRejected('unreadable'))),
        'image/jpeg',
        quality,
      ),
    );
  } finally {
    bitmap.close();
  }
}

export type PrepareOptions = {
  /** Project setting: keep the camera original instead of a resized copy. */
  keepOriginal?: boolean;
  /** Injected in tests (jsdom has no canvas). */
  resize?: typeof resizeImage;
};

export async function prepareMedia(
  file: File,
  options: PrepareOptions = {},
): Promise<PreparedMedia> {
  const kind = kindFor(file.type);
  if (!kind) throw new MediaRejected('type');
  const resize = options.resize ?? resizeImage;
  let blob: Blob = file;
  let thumbnail: Blob | null = null;
  if (kind === 'photo') {
    try {
      if (!options.keepOriginal) {
        const resized = await resize(file, MAX_EDGE_PX, JPEG_QUALITY);
        // A small JPEG can come out bigger re-encoded; keep whichever is smaller.
        if (resized.size < file.size || file.type !== 'image/jpeg') blob = resized;
      }
      thumbnail = await resize(file, THUMB_EDGE_PX, 0.7);
    } catch (error) {
      if (error instanceof MediaRejected) throw error;
      throw new MediaRejected('unreadable');
    }
  }
  const name = blob === file ? file.name : file.name.replace(/\.[^.]+$/, '') + '.jpg';
  return {
    blob,
    thumbnail,
    mime_type: blob.type || file.type,
    size: blob.size,
    sha256: await sha256Hex(blob),
    kind,
    file_name: name.slice(0, 200),
  };
}

/** Best-effort location, only if already permitted (never prompts mid-capture). */
export async function quickLocation(): Promise<{
  latitude: number;
  longitude: number;
  accuracy: number;
} | null> {
  try {
    if (!navigator.geolocation || !navigator.permissions) return null;
    const status = await navigator.permissions.query({ name: 'geolocation' });
    if (status.state !== 'granted') return null;
    return await new Promise((resolve) =>
      navigator.geolocation.getCurrentPosition(
        (position) =>
          resolve({
            latitude: Number(position.coords.latitude.toFixed(6)),
            longitude: Number(position.coords.longitude.toFixed(6)),
            accuracy: Number(position.coords.accuracy.toFixed(2)),
          }),
        () => resolve(null),
        { timeout: 3000, maximumAge: 60_000 },
      ),
    );
  } catch {
    return null;
  }
}
